// components/layout/Header.tsx
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Moon, Sun, Github, Menu, X } from 'lucide-react';
import { VisitorCounter } from '../hero/VisitorCounter';
import { Logo } from '../header/Logo';
import { Project } from '../../types/project';
import { Link } from 'react-router-dom';
import { LikeCounter } from '../header/LikeCounter';

interface HeaderProps {
  projects: Project[];
  onThemeToggle?: () => void;
  isDarkMode?: boolean;
}

export const Header = ({
  projects,
  onThemeToggle,
  isDarkMode = false,
}: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <>
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sticky top-0 z-50 bg-gray-100 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left section - Logo */}
            <div className="flex items-center">
              <Link to="/">
                <Logo />
              </Link>
            </div>

            {/* Right section - Stats and Actions */}
            <div className="flex items-center space-x-4">
              {/* Hide stats on mobile */}
              <div className="hidden sm:flex items-center space-x-4">
                <LikeCounter className="hidden sm:flex" />
                <VisitorCounter
                  type="total"
                  className="bg-white dark:bg-gray-800 shadow-sm hover:shadow-md
                          transition-all duration-300 rounded-lg"
                />
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="flex items-center gap-2 px-4 py-2 
                          bg-white dark:bg-gray-800 rounded-lg shadow-sm
                          text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700
                          transition-all duration-200"
                >
                  <span className="text-sm font-medium">
                    {projects.length} Projects
                  </span>
                </motion.div>
              </div>

              {/* Theme Toggle and GitHub - Always visible */}
              <div className="flex items-center space-x-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onThemeToggle}
                  className="p-2 rounded-lg bg-white dark:bg-gray-800 
                         text-gray-700 dark:text-gray-300 hover:bg-gray-50 
                         dark:hover:bg-gray-700 shadow-sm hover:shadow-md
                         transition-all duration-200"
                  aria-label="Toggle theme"
                >
                  {isDarkMode ? (
                    <Sun className="h-5 w-5" />
                  ) : (
                    <Moon className="h-5 w-5" />
                  )}
                </motion.button>

                <motion.a
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href="https://github.com/YahefuYusufu"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-white dark:bg-gray-800 
                         text-gray-700 dark:text-gray-300 hover:bg-gray-50
                         dark:hover:bg-gray-700 shadow-sm hover:shadow-md
                         transition-all duration-200"
                  aria-label="GitHub profile"
                >
                  <Github className="h-5 w-5" />
                </motion.a>

                {/* Mobile menu button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="lg:hidden p-2 rounded-lg bg-white dark:bg-gray-800 
                         text-gray-700 dark:text-gray-300 hover:bg-gray-50
                         dark:hover:bg-gray-700 shadow-sm hover:shadow-md
                         transition-all duration-200"
                  aria-label="Menu"
                >
                  {isMobileMenuOpen ? (
                    <X className="h-5 w-5" />
                  ) : (
                    <Menu className="h-5 w-5" />
                  )}
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Simplified Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden fixed inset-x-0 top-16 z-40 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-800"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 0.1 }}
              className="px-4 py-6 space-y-4"
            >
              {/* Mobile Stats */}
              <LikeCounter />
              <VisitorCounter
                type="total"
                className="bg-gray-50 dark:bg-gray-800 shadow-sm
                        transition-all duration-300 rounded-lg w-full"
              />
              <motion.div
                className="flex items-center justify-center gap-2 px-4 py-3 
                        bg-gray-50 dark:bg-gray-800 rounded-lg
                        text-gray-700 dark:text-gray-300 w-full"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="text-sm font-medium">
                  {projects.length} Projects
                </span>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
